import { uiPaper } from "shared/ui/uiSurface"
import { useAuth0 } from "@auth0/auth0-react"
import {
  DeliveryDining,
  Language,
  Link as LinkIcon,
  LocationOn,
  Logout,
  People,
  Person,
  Settings,
} from "@mui/icons-material"
import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { pathConstants } from "../pathConstants"

/** App-bar avatar menu (UI-DLV2-008): profile, language, account pages, logout. */
export const MainAppbarAvatarMenu: FC<{
  anchorEl: HTMLElement | null
  onClose: () => void
  fullName: string
}> = ({ anchorEl, onClose, fullName }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { logout } = useAuth0()

  const handleNavigate = (path: string) => {
    onClose()
    navigate(path)
  }

  const handleLanguageToggle = () => {
    const newLang = i18n.language === "fr" ? "en" : "fr"
    i18n.changeLanguage(newLang)
    localStorage.setItem("language", newLang)
    onClose()
  }

  const handleLogout = () => {
    onClose()
    logout({
      logoutParams: { returnTo: window.location.origin + "/login" },
    })
  }
  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      slotProps={{
        paper: {
          ...uiPaper("UI-DLV2-008"),
          sx: { width: 240, mt: 1 },
        },
      }}
    >
      {/* My Profile */}
      <MenuItem onClick={() => handleNavigate(pathConstants.profile)}>
        <ListItemIcon>
          <Person fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={fullName || t("profile")}
          secondary={t("menu.myProfile")}
          slotProps={{
            primary: {
              variant: "body2",
              fontWeight: 600,
              sx: {
                display: "-webkit-box !important",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                whiteSpace: "normal",
                wordBreak: "break-word",
              },
            },
            secondary: { variant: "caption" },
          }}
        />
      </MenuItem>

      {/* Language */}
      <MenuItem onClick={handleLanguageToggle}>
        <ListItemIcon>
          <Language fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={t("menu.language")}
          slotProps={{ primary: { variant: "body2" } }}
        />
        <Typography variant="caption" color="text.secondary">
          {i18n.language === "fr" ? "FR" : "EN"}
        </Typography>
      </MenuItem>

      {/* Configuration */}
      <MenuItem onClick={() => handleNavigate(pathConstants.configuration)}>
        <ListItemIcon>
          <Settings fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={t("menu.configuration")}
          slotProps={{ primary: { variant: "body2" } }}
        />
      </MenuItem>

      {/* Employees */}
      <MenuItem onClick={() => handleNavigate(pathConstants.employees)}>
        <ListItemIcon>
          <People fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={t("employees")}
          slotProps={{ primary: { variant: "body2" } }}
        />
      </MenuItem>

      <Divider />

      {/* Courier */}
      <MenuItem onClick={() => handleNavigate(pathConstants.couriers)}>
        <ListItemIcon>
          <DeliveryDining fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={t("menu.courier")}
          slotProps={{ primary: { variant: "body2" } }}
        />
      </MenuItem>

      {/* Provider Link */}
      <MenuItem onClick={() => handleNavigate(pathConstants.links)}>
        <ListItemIcon>
          <LinkIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={t("menu.providerLink")}
          slotProps={{ primary: { variant: "body2" } }}
        />
      </MenuItem>

      {/* Addresses (F094-F096) */}
      <MenuItem onClick={() => handleNavigate(pathConstants.addresses)}>
        <ListItemIcon>
          <LocationOn fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={t("menu.addresses")}
          slotProps={{ primary: { variant: "body2" } }}
        />
      </MenuItem>

      <Divider />

      {/* Logout */}
      <MenuItem onClick={handleLogout}>
        <ListItemIcon>
          <Logout fontSize="small" color="error" />
        </ListItemIcon>
        <ListItemText
          primary={t("logout")}
          slotProps={{
            primary: { variant: "body2", color: "error.main" },
          }}
        />
      </MenuItem>
    </Menu>
  )
}
